// Race lifecycle helpers.
//
// A race entry in coachingMemory has a `status` field:
//   'upcoming'  — future or today; default when status is missing on load
//   'completed' — the athlete finished the race
//   'dnf'       — did not finish
//   'dns'       — did not start
//
// The sanitizer runs at load time and after every merge. It:
//   - Fills a missing status ('upcoming' by default; 'completed' if the date
//     is strictly past AND the date is a strict ISO YYYY-MM-DD).
//   - Moves entries with a strict-ISO date more than 30 days past AND a
//     terminal status (completed / dnf / dns) into `pastRaces`.
//   - Merges duplicate-key entries (same across both arrays or within one).
//     "Archive location wins": if a key exists in both, the merged entry lands
//     in `pastRaces`.
//   - Leaves fuzzy-date entries (e.g. "Sep 2026") untouched — no auto-status,
//     no archive, no dedup by date. They stay wherever the caller put them
//     until the athlete pins down a real date.
//   - Emits a deterministic order: `races` sorted by date asc (undated last),
//     `pastRaces` sorted by date desc.
//
// The sanitizer is idempotent — running it twice yields the same result.

export const RACE_STATUS = Object.freeze({
  UPCOMING: 'upcoming',
  COMPLETED: 'completed',
  DNF: 'dnf',
  DNS: 'dns',
});

const TERMINAL_STATUSES = new Set([RACE_STATUS.COMPLETED, RACE_STATUS.DNF, RACE_STATUS.DNS]);
const ARCHIVE_AFTER_DAYS = 30;

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Parse a strict ISO YYYY-MM-DD as local midnight. `new Date('2026-07-05')`
// returns UTC midnight, which is the previous evening in every timezone west
// of UTC — that made the sanitizer flip a race to `completed` on race-day
// local morning. Parsing components manually pins us to the athlete's local
// day, matching how the UI countdown pill and Dashboard both compute
// `daysUntil`.
function parseIsoLocalMidnight(dateStr) {
  if (typeof dateStr !== 'string' || !ISO_DATE_RE.test(dateStr)) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  if (isNaN(dt.getTime())) return null;
  return dt;
}

function todayLocalMidnight(now = new Date()) {
  const t = new Date(now.getTime());
  t.setHours(0, 0, 0, 0);
  return t;
}

// Canonical dedup key: lowercased, trimmed, internal-whitespace-collapsed name
// + ISO date string as-supplied. Mirrors the server's `raceKeyFn`
// (`normText` = lowercase + trim + collapse whitespace) in
// api/coachingData/index.js — the two MUST stay in lockstep or a race whose
// name differs only by whitespace (e.g. extractor output "Holkham  Half" vs a
// client-typed "Holkham Half") would key differently on client vs server,
// letting a tombstone/re-learn timestamp silently fail to line up across the
// merge boundary.
export function raceKey(r) {
  const name = (r && typeof r.name === 'string' ? r.name : '').toLowerCase().trim().replace(/\s+/g, ' ');
  const date = (r && r.date) || '';
  return `${name}|${date}`;
}

// Merge two race entries with the same key.
//   - Fills missing scalar fields from the "other" side.
//   - For `status`, a terminal status (completed/dnf/dns) always wins over
//     'upcoming' or missing. If both are terminal, the more recent write wins
//     (best proxy: the one with a status field explicitly set — otherwise
//     falls back to `incoming`).
//   - Never loses `goal`, `distance`, `name` if either side has it.
export function mergeRaceEntries(existing, incoming) {
  const a = existing || {};
  const b = incoming || {};
  const out = { ...a, ...b };

  for (const field of ['name', 'date', 'distance', 'goal']) {
    if (!out[field] && a[field]) out[field] = a[field];
    if (!out[field] && b[field]) out[field] = b[field];
  }

  // Keep the most recent effective learning timestamp so a post-deletion
  // re-learn (which stamps `memoryUpdatedAt`) survives a merge with a stale
  // copy that lacks one.
  const at = new Date(a.memoryUpdatedAt || 0).getTime();
  const bt = new Date(b.memoryUpdatedAt || 0).getTime();
  const maxT = Math.max(Number.isFinite(at) ? at : 0, Number.isFinite(bt) ? bt : 0);
  if (maxT > 0) out.memoryUpdatedAt = new Date(maxT).toISOString();
  else delete out.memoryUpdatedAt;

  const aTerminal = TERMINAL_STATUSES.has(a.status);
  const bTerminal = TERMINAL_STATUSES.has(b.status);
  if (aTerminal && !bTerminal) out.status = a.status;
  else if (bTerminal && !aTerminal) out.status = b.status;
  else if (aTerminal && bTerminal) out.status = b.status;
  else out.status = b.status || a.status || RACE_STATUS.UPCOMING;

  return out;
}

// Pool duplicate-keyed entries across both arrays and within each.
// Returns { byKey, order } — byKey maps race key → { merged, inPastRaces }
// where `inPastRaces` is true if the key appeared in `pastRacesIn` at all.
function poolByKey(racesIn, pastRacesIn) {
  const byKey = new Map();
  const order = [];

  const absorb = (list, isPast) => {
    for (const r of Array.isArray(list) ? list : []) {
      if (!r || typeof r !== 'object') continue;
      const key = raceKey(r);
      const prev = byKey.get(key);
      if (prev) {
        prev.merged = mergeRaceEntries(prev.merged, r);
        prev.inPastRaces = prev.inPastRaces || isPast;
      } else {
        byKey.set(key, { merged: { ...r }, inPastRaces: isPast });
        order.push(key);
      }
    }
  };

  absorb(racesIn, false);
  absorb(pastRacesIn, true);
  return { byKey, order };
}

// Compute the sanitized { races, pastRaces } for a memory object.
// - `now` defaults to `new Date()`; injectable for tests and briefings.
// - Fuzzy-date entries pass through untouched (no auto-status, no archive).
// - Non-ISO or unparseable dates likewise pass through untouched.
export function sanitizeRaces(memory, { now = new Date() } = {}) {
  const racesIn = memory && Array.isArray(memory.races) ? memory.races : [];
  const pastIn = memory && Array.isArray(memory.pastRaces) ? memory.pastRaces : [];
  if (racesIn.length === 0 && pastIn.length === 0) {
    return { races: [], pastRaces: [] };
  }

  const today = todayLocalMidnight(now);
  const { byKey, order } = poolByKey(racesIn, pastIn);

  const races = [];
  const pastRaces = [];

  for (const key of order) {
    const { merged, inPastRaces } = byKey.get(key);
    const parsed = parseIsoLocalMidnight(merged.date);

    // Fuzzy or missing date: leave status alone, respect archive location, no
    // automatic transitions.
    if (!parsed) {
      if (!merged.status) merged.status = RACE_STATUS.UPCOMING;
      if (inPastRaces) pastRaces.push(merged);
      else races.push(merged);
      continue;
    }

    const diffDays = Math.round((parsed.getTime() - today.getTime()) / 86400000);

    // Auto-fill status for entries with no status yet.
    if (!merged.status) {
      merged.status = diffDays >= 0 ? RACE_STATUS.UPCOMING : RACE_STATUS.COMPLETED;
    } else if (merged.status === RACE_STATUS.UPCOMING && diffDays < 0) {
      // A race whose date has passed but is still tagged 'upcoming' — flip
      // it. Never overwrite a terminal status (dnf/dns), even if it's on a
      // future date (athlete pre-marked it as DNS, say).
      merged.status = RACE_STATUS.COMPLETED;
    }

    const terminal = TERMINAL_STATUSES.has(merged.status);
    const shouldArchive = terminal && diffDays < -ARCHIVE_AFTER_DAYS;

    if (shouldArchive || inPastRaces) pastRaces.push(merged);
    else races.push(merged);
  }

  // Deterministic ordering so `memoryEquivalent`'s JSON.stringify compare
  // doesn't false-negative across polls that happen to produce the same set
  // in a different order.
  const cmpDateAsc = (a, b) => {
    const da = parseIsoLocalMidnight(a.date);
    const db = parseIsoLocalMidnight(b.date);
    if (!da && !db) return raceKey(a).localeCompare(raceKey(b));
    if (!da) return 1;   // undated last
    if (!db) return -1;
    if (da.getTime() !== db.getTime()) return da.getTime() - db.getTime();
    return raceKey(a).localeCompare(raceKey(b));
  };
  const cmpDateDesc = (a, b) => -cmpDateAsc(a, b);

  races.sort(cmpDateAsc);
  pastRaces.sort(cmpDateDesc);

  return { races, pastRaces };
}

// Convenience: return upcoming races only — future/today AND not DNF/DNS.
// Fuzzy-date entries with status 'upcoming' (or missing) are treated as
// upcoming (we can't prove otherwise).
export function getUpcomingRaces(memory, { now = new Date() } = {}) {
  const src = memory && Array.isArray(memory.races) ? memory.races : [];
  const today = todayLocalMidnight(now);
  return src.filter(r => {
    if (!r || typeof r !== 'object') return false;
    const status = r.status || RACE_STATUS.UPCOMING;
    if (status !== RACE_STATUS.UPCOMING) return false;
    const parsed = parseIsoLocalMidnight(r.date);
    if (!parsed) return true;  // fuzzy date, keep it
    return parsed.getTime() >= today.getTime();
  });
}

// Convenience: recently completed races (past N days, terminal status).
// Searches both `races` and `pastRaces` — usually the target lands in `races`
// because archival is at 30 days, but a stale merge might leave a 20d-past
// race in `pastRaces`.
export function getRecentlyCompletedRaces(memory, { days = 7, now = new Date() } = {}) {
  const today = todayLocalMidnight(now);
  const cutoffMs = today.getTime() - days * 86400000;
  const scan = [];
  if (memory && Array.isArray(memory.races)) scan.push(...memory.races);
  if (memory && Array.isArray(memory.pastRaces)) scan.push(...memory.pastRaces);
  const out = [];
  for (const r of scan) {
    if (!r || typeof r !== 'object') continue;
    if (!TERMINAL_STATUSES.has(r.status)) continue;
    const parsed = parseIsoLocalMidnight(r.date);
    if (!parsed) continue;
    const t = parsed.getTime();
    if (t > today.getTime()) continue;   // future somehow — skip
    if (t < cutoffMs) continue;
    out.push({ ...r, daysAgo: Math.max(0, Math.round((today.getTime() - t) / 86400000)) });
  }
  return out.sort((a, b) => a.daysAgo - b.daysAgo);
}

// Convenience: flat list of all races (upcoming + past) for label lookup,
// e.g. TrainingPlanPage matching phases by `targetRaceId` needs to render the
// race name for phases pinned to archived races.
export function getAllRacesForLookup(memory) {
  const a = memory && Array.isArray(memory.races) ? memory.races : [];
  const b = memory && Array.isArray(memory.pastRaces) ? memory.pastRaces : [];
  return [...a, ...b];
}
